/**
 * The form a playbook puts in front of a person — a Work List assignee inside
 * the application, or an outsider on a one-time link. One component for both
 * so the two can never ask differently.
 *
 * ``mode="approve_reject"`` adds the Approve / Reject decision (and an
 * optional comment) above any fields the step declares.
 */
import { useState, type CSSProperties } from 'react'
import type { FormField } from './contract'
import { C, inputStyle, labelStyle } from '../jobs/ui'

export interface PlaybookFormProps {
  title?: string
  intro?: string
  fields: FormField[]
  mode?: 'input' | 'approve_reject' | string
  context?: Record<string, unknown>
  submitLabel?: string
  onSubmit(answer: Record<string, unknown>): Promise<void>
}

const btn = (primary: boolean, tone: string = C.accent): CSSProperties => ({
  height: 36, padding: '0 16px', borderRadius: 8, fontSize: '0.9375rem', fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${primary ? tone : C.line}`,
  background: primary ? tone : 'transparent', color: primary ? '#fff' : C.ink,
})

function renderValue(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export default function PlaybookForm({
  title, intro, fields, mode = 'input', context, submitLabel, onSubmit,
}: PlaybookFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [decision, setDecision] = useState<'' | 'approved' | 'rejected'>('')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const approval = mode === 'approve_reject'

  const set = (name: string, v: unknown) => setValues((cur) => ({ ...cur, [name]: v }))

  const submit = async () => {
    setError('')
    const missing = fields.filter((f) => f.required && (values[f.name] == null || values[f.name] === ''))
    if (missing.length) {
      setError(`Please fill in: ${missing.map((f) => f.label || f.name).join(', ')}.`)
      return
    }
    if (approval && !decision) {
      setError('Choose Approve or Reject.')
      return
    }
    setBusy(true)
    try {
      await onSubmit({ ...values, ...(approval ? { decision, comment } : {}) })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your answer could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  const ctxEntries = Object.entries(context || {}).filter(([k]) => !k.startsWith('_'))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: C.ink }}>
      {title && <div style={{ fontSize: '1.1125rem', fontWeight: 700 }}>{title}</div>}
      {intro && <div style={{ fontSize: '0.9375rem', color: C.ink2, lineHeight: 1.5 }}>{intro}</div>}
      {ctxEntries.length > 0 && (
        <div style={{
          border: `1px solid ${C.line}`, borderRadius: 8, background: C.s2,
          padding: '8px 12px', display: 'grid', gridTemplateColumns: 'minmax(90px, auto) 1fr',
          gap: '4px 12px', fontSize: '0.875rem',
        }}>
          {ctxEntries.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <span style={{ color: C.ink2 }}>{k.replace(/_/g, ' ')}</span>
              <span style={{ wordBreak: 'break-word' }}>{renderValue(v)}</span>
            </div>
          ))}
        </div>
      )}
      {approval && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setDecision('approved')}
            style={btn(decision === 'approved', C.ok)}>Approve</button>
          <button type="button" onClick={() => setDecision('rejected')}
            style={btn(decision === 'rejected', C.err)}>Reject</button>
        </div>
      )}
      {fields.map((f) => (
        <label key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>{f.label || f.name}{f.required ? ' *' : ''}</span>
          {f.type === 'textarea' ? (
            <textarea value={String(values[f.name] ?? '')} rows={4}
              onChange={(e) => set(f.name, e.target.value)}
              style={{ ...inputStyle, height: 'auto', padding: 8, resize: 'vertical' }} />
          ) : f.type === 'boolean' ? (
            <input type="checkbox" checked={!!values[f.name]}
              onChange={(e) => set(f.name, e.target.checked)}
              style={{ width: 18, height: 18, accentColor: 'var(--color-accent, #0061D0)' }} />
          ) : f.type === 'select' ? (
            <select value={String(values[f.name] ?? '')} onChange={(e) => set(f.name, e.target.value)}
              style={inputStyle}>
              <option value="">Choose…</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
              value={String(values[f.name] ?? '')}
              onChange={(e) => set(f.name, f.type === 'number' && e.target.value !== ''
                ? Number(e.target.value) : e.target.value)}
              style={inputStyle} />
          )}
          {f.help && <span style={{ fontSize: '0.8125rem', color: C.faint }}>{f.help}</span>}
        </label>
      ))}
      {approval && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Comment</span>
          <textarea value={comment} rows={3} onChange={(e) => setComment(e.target.value)}
            style={{ ...inputStyle, height: 'auto', padding: 8, resize: 'vertical' }} />
        </label>
      )}
      {error && (
        <div role="alert" style={{
          background: C.errWeak, color: C.err, borderRadius: 8, padding: '8px 12px',
          fontSize: '0.875rem',
        }}>{error}</div>
      )}
      <div>
        <button type="button" disabled={busy} onClick={() => void submit()}
          style={{ ...btn(true), opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Sending…' : submitLabel || 'Send'}
        </button>
      </div>
    </div>
  )
}
